import React, { useState, useEffect } from 'react';
import {
  Heart,
  MessageSquare,
  Sparkles
} from 'lucide-react';
import { patchCelebrationData } from './celebrationsData';
import { fireConfetti } from './confettiUtil';

const config = require('../../../config.json');

export default function CelebrationCard({ celebrant, onOpenWorkspace, onUpdateCelebrant }) {
  const [likeCount, setLikeCount] = useState(0);
  const [greetingsCount, setGreetingsCount] = useState(0);
  const [isLoved, setIsLoved] = useState(false);
  const [floatingHearts, setFloatingHearts] = useState([]);

  useEffect(() => {
    if (celebrant) {
      setLikeCount(Number(celebrant.loveCount || 0));
      const gCount = celebrant.totalGreetings !== undefined
        ? Number(celebrant.totalGreetings)
        : (Array.isArray(celebrant.greetings) ? celebrant.greetings.length : (Array.isArray(celebrant.initialGreetings) ? celebrant.initialGreetings.length : 0));
      setGreetingsCount(gCount);
    }
  }, [celebrant]);

  const handleHeartClick = (e) => {
    e.stopPropagation();
    const newLikes = likeCount + 1;
    setLikeCount(newLikes);
    setIsLoved(true);

    if (onUpdateCelebrant) {
      onUpdateCelebrant(celebrant.id || celebrant.registrationNo, { loveCount: newLikes });
    }

    // Dynamic backend sync with DynamoDB via PATCH /website/celebration
    const greetings = celebrant.greetings || celebrant.initialGreetings || [];
    patchCelebrationData(celebrant, { loveCount: newLikes }, greetings, config);

    // Trigger floating heart animation
    const rect = e.currentTarget.getBoundingClientRect();
    const newHeart = {
      id: Date.now(),
      left: e.clientX - rect.left - 10,
      top: e.clientY - rect.top - 20
    };
    setFloatingHearts((prev) => [...prev, newHeart]);
    setTimeout(() => {
      setFloatingHearts((prev) => prev.filter((h) => h.id !== newHeart.id));
    }, 1200);

    // Particle burst
    fireConfetti({
      particleCount: 25,
      spread: 45,
      origin: { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }
    });
  };

  const handleConfettiQuick = (e) => {
    e.stopPropagation();
    fireConfetti({
      particleCount: 65,
      spread: 75,
      origin: { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }
    });
  };

  // Occasion icon mapping
  const getOccasionIcon = (type) => {
    switch (type) {
      case 'birthday': return '🎂';
      case 'ordination': return '🕊️';
      case 'feast': return '🌟';
      case 'milestone': return '🏆';
      default: return '🎉';
    }
  };

  return (
    <div className="vj-cel-card">
      {/* Top Banner & Celebrant Avatar */}
      <div className="vj-cel-card-top">
        <span className={`vj-occasion-tag ${celebrant.occasionType}`}>
          <span>{getOccasionIcon(celebrant.occasionType)}</span>
          {celebrant.badge || celebrant.occasionTitle}
        </span>

        <div className="vj-cel-avatar-wrapper">
          <div className="vj-cel-avatar-ring">
            <img
              src={celebrant.photo}
              alt={celebrant.name}
              className="vj-cel-avatar-img"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&q=80&w=400';
              }}
            />
          </div>
          <div className="vj-cel-float-badge">
            {getOccasionIcon(celebrant.occasionType)}
          </div>
        </div>

        <h4 className="vj-cel-name">{celebrant.name}</h4>
        <p className="vj-cel-role">{celebrant.role}</p>
        <p className="vj-cel-dept">{celebrant.department}</p>
      </div>

      {/* Card Body & Quote */}
      <div className="vj-cel-card-body">
        <div className="vj-cel-quote-box">
          <p className="vj-cel-quote-text">“{celebrant.quote}”</p>
        </div>

        {/* Reaction Bar */}
        <div className="vj-cel-reactions">
          <div className="position-relative">
            <button
              className={`vj-reaction-btn ${isLoved ? 'active' : ''}`}
              onClick={handleHeartClick}
              title="Send Love & Blessings"
            >
              <Heart size={16} fill={isLoved ? '#dc2626' : 'none'} className={isLoved ? 'text-danger' : ''} />
              <span>{likeCount}</span>
            </button>

            {/* Floating Hearts Animation */}
            {floatingHearts.map((h) => (
              <span
                key={h.id}
                className="vj-floating-emoji"
                style={{ left: `${h.left}px`, top: `${h.top}px` }}
              >
                ❤️
              </span>
            ))}
          </div>

          <button
            className="vj-greetings-count-badge btn border-0"
            onClick={() => onOpenWorkspace(celebrant, 'guestbook')}
          >
            <MessageSquare size={14} />
            <span>{greetingsCount} Wishes</span>
          </button>
        </div>

        {/* Action Buttons */}
        <div className="vj-cel-card-actions">
          <button
            className="vj-btn-open-card"
            onClick={() => onOpenWorkspace(celebrant, 'ecard')}
          >
            <Sparkles size={16} /> Open E-Card & Workspace
          </button>
          <button
            className="vj-btn-confetti-quick"
            onClick={handleConfettiQuick}
            title="Fire Confetti!"
          >
            🎉
          </button>
        </div>
      </div>
    </div>
  );
}
